// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import ShaderLibExtra from '../passpack/ShaderLibExtra';
import FullscreenPass from '../renderer/pass/FullscreenPass';
import BloomPass from '../passpack/BloomPass';
import BlurPass from '../passpack/BlurPass';
import DogPass from '../passpack/DogPass';
import MotionBlurPass from '../passpack/MotionBlurPass';
import SsaoPass from '../passpack/SsaoPass';
import ObjectUtils from '../util/ObjectUtils';
import Capabilities from '../renderer/Capabilities';

class Bloom extends BloomPass {
  constructor(id) {
    super();
    this.id = id;
  }

  update(config) {
    const options = config.options || {};
    if (options.opacity !== undefined) {
      this.copyMaterial.uniforms.opacity = options.opacity / 100;
    }
    if (options.size !== undefined) {
      this.convolutionMaterial.uniforms.size = options.size;
    }
    if (options.brightness !== undefined) {
      this.bcMaterial.uniforms.brightness = options.brightness / 100;
    }
    if (options.contrast !== undefined) {
      this.bcMaterial.uniforms.contrast = options.contrast / 100;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Bloom.label = 'Bloom';
Bloom.options = [
  {
    key: 'opacity',
    name: 'Opacity',
    type: 'int',
    control: 'slider',
    min: 0,
    max: 100,
    default: 100,
  },
  {
    key: 'size',
    name: 'Size',
    type: 'float',
    control: 'slider',
    min: 0,
    max: 10,
    decimals: 1,
    default: 2,
  },
  {
    key: 'brightness',
    name: 'Gain',
    type: 'int',
    control: 'slider',
    min: -100,
    max: 100,
    default: 0,
  },
  {
    key: 'contrast',
    name: 'Intensity',
    type: 'int',
    control: 'slider',
    min: -100,
    max: 100,
    default: 0,
  },
];

class DiffOfGaussians extends DogPass {
  constructor(id) {
    super(arguments);
    this.id = id;
  }

  update(config) {
    const options = config.options || {};

    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }

    if (options.sigma !== undefined) {
      this.updateSigma(options.sigma);
    }

    if (options.threshold !== undefined) {
      this.updateThreshold(options.threshold);
    }

    if (options.edgeColor !== undefined) {
      this.updateEdgeColor(options.edgeColor);
    }

    if (options.backgroundColor !== undefined) {
      this.updateBackgroundColor(options.backgroundColor);
    }

    if (options.backgroundMix !== undefined) {
      this.updateBackgroundMix(options.backgroundMix);
    }
  }
}

DiffOfGaussians.label = 'Edge detect';
DiffOfGaussians.options = [
  {
    key: 'sigma',
    name: 'Gauss Sigma',
    type: 'float',
    control: 'slider',
    min: 0.01,
    max: 1.7,
    decimals: 2,
    default: 0.6,
  },
  {
    key: 'threshold',
    name: 'Threshold',
    type: 'float',
    control: 'slider',
    min: 0.00000000000001,
    max: 0.11,
    decimals: 20,
    default: 0.005,
  },
  {
    key: 'backgroundMix',
    name: 'Background %',
    type: 'float',
    control: 'slider',
    min: 0.0,
    max: 1.0,
    decimals: 2,
    default: 0.0,
  },
  {
    key: 'edgeColor',
    name: 'Edge Color',
    type: 'vec3',
    control: 'color',
    default: [0.0, 1.0, 0.0],
  },
  {
    key: 'backgroundColor',
    name: 'Background Color',
    type: 'vec3',
    control: 'color',
    default: [0.0, 0.0, 0.0],
  },
];

class Blur extends BlurPass {
  constructor(id) {
    super(arguments);
    this.id = id;
  }

  update(config) {
    const options = config.options || {};
    if (options.opacity !== undefined) {
      this.copyMaterial.uniforms.opacity = options.opacity / 100;
    }
    if (options.size !== undefined) {
      this.blurX = [0.001953125 * options.size, 0.0];
      this.blurY = [0.0, 0.001953125 * options.size];
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Blur.label = 'Blur';
Blur.options = [
  {
    key: 'opacity',
    name: 'Amount',
    type: 'int',
    control: 'slider',
    min: 0,
    max: 100,
    default: 100,
  },
  {
    key: 'size',
    name: 'Size',
    type: 'float',
    control: 'slider',
    min: 0.0,
    max: 5,
    decimals: 1,
    default: 1,
  },
];

class Vignette extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.vignette));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.offset !== undefined) {
      shader.uniforms.offset = options.offset;
    }
    if (options.darkness !== undefined) {
      shader.uniforms.darkness = options.darkness;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Vignette.prototype.construcor = Vignette;

Vignette.label = 'Vignette';
Vignette.options = [
  {
    key: 'offset',
    type: 'float',
    control: 'slider',
    name: 'Offset',
    min: 0,
    max: 10,
    decimals: 1,
    default: 1,
  },
  {
    key: 'darkness',
    type: 'float',
    control: 'slider',
    name: 'Darkness',
    min: 0,
    max: 2,
    decimals: 2,
    default: 1.5,
  },
];

class Sepia extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.sepia));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    if (options.amount !== undefined) {
      this.material.uniforms.amount = options.amount / 100;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Sepia.label = 'Sepia';
Sepia.options = [
  {
    key: 'amount',
    name: 'Amount',
    type: 'int',
    control: 'slider',
    min: 0,
    max: 100,
    default: 100,
  },
];

class Grain extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.film));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.nIntensity !== undefined) {
      shader.uniforms.nIntensity = options.nIntensity / 100;
    }
    if (options.sIntensity !== undefined) {
      shader.uniforms.sIntensity = options.sIntensity / 100;
    }
    if (options.sCount !== undefined) {
      shader.uniforms.sCount = options.sCount;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Grain.label = 'Film Grain';
Grain.options = [{
  key: 'nIntensity',
  type: 'int',
  control: 'slider',
  name: 'Noise',
  min: 0,
  max: 100,
  default: 50,
}, {
  key: 'sIntensity',
  type: 'int',
  control: 'slider',
  name: 'Line Intensity',
  min: 0,
  max: 100,
  default: 50,
}, {
  key: 'sCount',
  type: 'int',
  control: 'slider',
  name: 'Line Count',
  min: 1,
  max: 4096,
  default: 1024,
}];

class Noise extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.noise));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.nIntensity !== undefined) {
      shader.uniforms.nIntensity = options.nIntensity / 100;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Noise.label = 'Noise';
Noise.options = [
  {
    key: 'nIntensity',
    type: 'int',
    control: 'slider',
    name: 'Noise',
    min: 0,
    max: 100,
    default: 50,
  },
];

class RgbShift extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.rgbshift));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.amount !== undefined) {
      shader.uniforms.amount = options.amount;
    }
    if (options.angle !== undefined) {
      shader.uniforms.angle = options.angle;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

RgbShift.label = 'RgbShift';
RgbShift.options = [
  {
    key: 'amount',
    type: 'float',
    control: 'slider',
    name: 'Amount',
    min: 0,
    max: 0.05,
    decimals: 3,
    default: 0.005,
  },
  {
    key: 'angle',
    type: 'float',
    control: 'slider',
    name: 'Angle',
    min: 0,
    max: 6.28,
    decimals: 1,
    default: 0,
  },
];

class Bleach extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.bleachbypass));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.opacity !== undefined) {
      shader.uniforms.opacity = options.opacity;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Bleach.label = 'Bleach';
Bleach.options = [
  {
    key: 'opacity',
    type: 'float',
    control: 'slider',
    name: 'Opacity',
    min: 0,
    max: 1,
    decimals: 2,
    default: 1,
  },
];

class HSB extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.hsb));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.hue !== undefined) {
      shader.uniforms.hue = options.hue;
    }
    if (options.saturation !== undefined) {
      shader.uniforms.saturation = options.saturation;
    }
    if (options.brightness !== undefined) {
      shader.uniforms.brightness = options.brightness;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

HSB.label = 'HSB';
HSB.options = [
  {
    key: 'hue',
    type: 'float',
    control: 'slider',
    name: 'Hue',
    min: -1,
    max: 1,
    decimals: 2,
    default: 0,
  },
  {
    key: 'saturation',
    type: 'float',
    control: 'slider',
    name: 'Saturation',
    min: -1,
    max: 1,
    decimals: 2,
    default: 0,
  },
  {
    key: 'brightness',
    type: 'float',
    control: 'slider',
    name: 'Brightness',
    min: -1,
    max: 1,
    decimals: 2,
    default: 0,
  },
];

class Colorify extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.colorify));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.color !== undefined) {
      shader.uniforms.color = options.color;
    }
    if (options.amount !== undefined) {
      shader.uniforms.amount = options.amount;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Colorify.label = 'Tint';
Colorify.options = [
  {
    key: 'color',
    type: 'vec3',
    control: 'color',
    name: 'Color',
    default: [1.0, 1.0, 1.0],
  },
  {
    key: 'amount',
    type: 'float',
    control: 'slider',
    name: 'Amount',
    min: 0,
    max: 1,
    decimals: 2,
    default: 1,
  },
];

class Hatch extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.hatch));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.width !== undefined) {
      shader.uniforms.width = options.width;
    }
    if (options.spread !== undefined) {
      shader.uniforms.spread = options.spread;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Hatch.label = 'Hatch';
Hatch.options = [
  {
    key: 'width',
    type: 'float',
    control: 'slider',
    name: 'Width',
    min: 0,
    max: 10,
    decimals: 1,
    default: 2,
  },
  {
    key: 'spread',
    type: 'int',
    control: 'slider',
    name: 'Spread',
    min: 1,
    max: 50,
    default: 8,
  },
];

class Dot extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.dotscreen));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.angle !== undefined) {
      shader.uniforms.angle = options.angle;
    }
    if (options.scale !== undefined) {
      shader.uniforms.scale = options.scale;
    }
    if (options.sizex !== undefined) {
      shader.uniforms.tSize[0] = options.sizex;
    }
    if (options.sizey !== undefined) {
      shader.uniforms.tSize[1] = options.sizey;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Dot.label = 'Dot';
Dot.options = [
  {
    key: 'angle',
    type: 'float',
    control: 'slider',
    name: 'Angle',
    min: 0,
    max: 10,
    decimals: 2,
    default: 1.57,
  },
  {
    key: 'scale',
    type: 'float',
    control: 'slider',
    name: 'Scale',
    min: 0,
    max: 10,
    decimals: 2,
    default: 1,
  },
  {
    key: 'sizex',
    type: 'int',
    control: 'slider',
    name: 'SizeX',
    min: 0,
    max: 1024,
    default: 256,
  },
  {
    key: 'sizey',
    type: 'int',
    control: 'slider',
    name: 'SizeY',
    min: 0,
    max: 1024,
    default: 256,
  },
];

class Contrast extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.brightnesscontrast));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.brightness !== undefined) {
      shader.uniforms.brightness = options.brightness;
    }
    if (options.contrast !== undefined) {
      shader.uniforms.contrast = options.contrast;
    }
    if (options.saturation !== undefined) {
      shader.uniforms.saturation = options.saturation;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Contrast.label = 'Contrast';
Contrast.options = [
  {
    key: 'brightness',
    type: 'float',
    control: 'slider',
    name: 'Brightness',
    min: -1,
    max: 1,
    decimals: 2,
    default: 0,
  },
  {
    key: 'contrast',
    type: 'float',
    control: 'slider',
    name: 'Contrast',
    min: 0,
    max: 1,
    default: 0,
  },
  {
    key: 'saturation',
    type: 'float',
    control: 'slider',
    name: 'Saturation',
    min: -1,
    max: 1,
    decimals: 2,
    default: 0,
  },
];

class MotionBlur extends MotionBlurPass {
  constructor(id) {
    super();
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.inPass.material.shader;
    if (options.blend !== undefined) {
      shader.uniforms.blend = options.blend;
    }
    if (options.scale !== undefined) {
      shader.uniforms.scale = options.scale;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

MotionBlur.label = 'Motion Blur';

MotionBlur.options = [
  {
    key: 'blend',
    type: 'float',
    control: 'slider',
    name: 'Amount',
    min: 0,
    max: 1,
    default: 0.5,
  },
  {
    key: 'scale',
    type: 'float',
    name: 'Scale',
    min: 0.2,
    default: 1,
    scale: 0.01,
  },
];

class Antialias extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.antialias));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.span !== undefined) {
      shader.uniforms.FXAA_SPAN_MAX = options.span;
      shader.uniforms.FXAA_REDUCE_MUL = 1 / options.span;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Antialias.label = 'Antialias';
Antialias.options = [
  {
    key: 'span',
    type: 'int',
    control: 'slider',
    name: 'Span',
    min: 0,
    max: 16,
    default: 8,
  },
];

class Radial extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.radial));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.offset !== undefined) {
      shader.uniforms.offset = options.offset;
    }
    if (options.multiplier !== undefined) {
      shader.uniforms.multiplier = options.multiplier;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Radial.label = 'Radial';
Radial.options = [
  {
    key: 'offset',
    type: 'float',
    control: 'slider',
    name: 'Offset',
    min: -1,
    max: 1,
    decimals: 2,
    default: -0.5,
  },
  {
    key: 'multiplier',
    type: 'float',
    control: 'slider',
    name: 'Multiplier',
    min: -1,
    max: 1,
    decimals: 2,
    default: 0.75,
  },
];

//TODO: add visual test for overlay
class Overlay extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.overlay));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.blendmode !== undefined) {
      const newBlendMode = Overlay.blendmodes.indexOf(options.blendmode);
      if (newBlendMode !== shader.defines.OVERLAY_TYPE) {
        shader.setDefine('OVERLAY_TYPE', newBlendMode);
        shader.uniforms.amount = options.amount - 0.01;
      }
    }
    if (options.amount !== undefined) {
      shader.uniforms.amount = options.amount;
    }
    if (options.url != null) {
      this.material.setTexture('OVERLAY_MAP', options.url);
    } else {
      this.material.removeTexture('OVERLAY_MAP');
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Overlay.blendmodes = [
  'Normal',
  'Lighten',
  'Darken',
  'Multiply',
  'Average',
  'Add',
  'Substract',
  'Difference',
  'Negation',
  'Exclusion',
  'Screen',
  'Overlay',
  'SoftLight',
  'HardLight',
  'ColorDodge',
  'ColorBurn',
  'LinearLight',
  'VividLight',
  'PinLight',
  'HardMix',
  'Reflect',
  'Glow',
  'Phoenix',
];

Overlay.label = 'Overlay';
Overlay.options = [
  {
    key: 'url',
    name: 'Texture',
    type: 'texture',
    default: { enabled: true },
  },
  {
    key: 'blendmode',
    name: 'Blend Mode',
    type: 'string',
    control: 'select',
    options: Overlay.blendmodes,
    default: 'Normal',
  },
  {
    key: 'amount',
    name: 'Amount',
    type: 'float',
    control: 'slider',
    min: 0,
    max: 1,
    decimals: 2,
    default: 1,
  },
];

class Levels extends FullscreenPass {
  constructor(id) {
    super(ObjectUtils.deepClone(ShaderLibExtra.levels));
    this.id = id;
  }

  update(config) {
    const options = config.options;
    const shader = this.material.shader;
    if (options.gamma !== undefined) {
      shader.uniforms.gamma = options.gamma;
    }
    if (options.gamma !== undefined) {
      shader.uniforms.gamma = options.gamma;
    }
    if (options.minInput !== undefined) {
      shader.uniforms.minInput = options.minInput;
    }
    if (options.maxInput !== undefined) {
      shader.uniforms.maxInput = options.maxInput;
    }
    if (options.minOutput !== undefined) {
      shader.uniforms.minOutput = options.minOutput;
    }
    if (options.maxOutput !== undefined) {
      shader.uniforms.maxOutput = options.maxOutput;
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

Levels.label = 'Levels';
Levels.options = [
  {
    key: 'gamma',
    type: 'float',
    control: 'slider',
    name: 'Gamma',
    min: 0,
    max: 5,
    decimals: 2,
    default: 1,
  },
  {
    key: 'minInput',
    type: 'float',
    control: 'slider',
    name: 'Min Input',
    min: 0,
    max: 1,
    decimals: 2,
    default: 0,
  },
  {
    key: 'maxInput',
    type: 'float',
    control: 'slider',
    name: 'Max Input',
    min: 0,
    max: 1,
    decimals: 2,
    default: 1,
  },
  {
    key: 'minOutput',
    type: 'float',
    control: 'slider',
    name: 'Min Output',
    min: 0,
    max: 1,
    decimals: 2,
    default: 0,
  },
  {
    key: 'maxOutput',
    type: 'float',
    control: 'slider',
    name: 'Max Output',
    min: 0,
    max: 1,
    decimals: 2,
    default: 1,
  },
];

class SSAO extends SsaoPass {
  constructor(id, renderList) {
    const options = {
      variant: true,
      sampleSize: 32,
      downsampleAmount: 1
    };
    if (!Capabilities.DrawBuffers) {
      options.variant = false;
    } else if (Capabilities.isMobile || Capabilities.isTablet) {
      options.downsampleAmount = 2;
    }
    super(renderList, options);
    this.id = id;
  }

  update(config) {
    const options = config.options;
    if (options.radius !== undefined) {
      this.setRadius(options.radius);
    }
    if (config.enabled !== undefined) {
      this.enabled = config.enabled;
    }
  }
}

SSAO.label = 'SSAO';
SSAO.options = [
  {
    key: 'radius',
    type: 'float',
    control: 'slider',
    name: 'Radius',
    min: 0,
    max: 1,
    decimals: 2,
    default: 0.3,
  }
];

export default {
  Bloom,
  Blur,
  Vignette,
  Sepia,
  Grain,
  Noise,
  RgbShift,
  Bleach,
  HSB,
  Colorify,
  Hatch,
  Dot,
  Contrast,
  DiffOfGaussians,
  MotionBlur,
  Antialias,
  Radial,
  Overlay,
  Levels,
  SSAO,
};
